import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Shield, TrendingUp, Globe, Scale, Wifi, Building } from 'lucide-react';

const services = [
  {
    number: '01',
    title: 'Wealth Protection & Asset Structuring',
    description:
      'Sophisticated strategies to safeguard and structure international assets with discretion and precision.',
    icon: Shield,
  },
  {
    number: '02',
    title: 'International Trust & Banking Advisory',
    description:
      'Expert guidance on trust structures, banking relationships, and fiduciary arrangements across jurisdictions.',
    icon: Building,
  },
  {
    number: '03',
    title: 'Cross-Border Business Strategy',
    description:
      'Strategic counsel for international business operations, structuring, and regulatory navigation.',
    icon: Globe,
  },
  {
    number: '04',
    title: 'Cross-Border Dispute Resolution',
    description:
      'Resolving intricate international disputes with tact, experience, and comprehensive legal acumen.',
    icon: Scale,
  },
  {
    number: '05',
    title: 'IT/Telecommunications Legal Advisory',
    description:
      'Specialized expertise in technology law, telecommunications regulation, and digital asset matters.',
    icon: Wifi,
  },
  {
    number: '06',
    title: 'International Banking Dispute Resolution',
    description:
      'Expert resolution of intricate banking disputes, regulatory matters, and financial institution relationships.',
    icon: TrendingUp,
  },
];

const ServiceCard = ({ service, index }) => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const Icon = service.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{
        duration: 0.8,
        delay: index * 0.1,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group relative bg-gradient-to-br from-[#1a2332]/50 to-[#0f1419]/50 p-8 md:p-10 border border-[#c9a961]/20 hover:border-[#c9a961]/50 transition-all duration-500 overflow-hidden"
    >
      {/* Background effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#c9a961]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10">
        {/* Number */}
        <div className="flex items-start justify-between mb-6">
          <span className="font-serif text-6xl text-[#c9a961]/30 group-hover:text-[#c9a961]/50 transition-colors duration-500">
            {service.number}
          </span>
          <Icon className="w-10 h-10 text-[#c9a961] opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
        </div>

        {/* Title */}
        <h3 className="font-serif text-2xl md:text-3xl text-white mb-4 leading-tight group-hover:text-[#c9a961] transition-colors duration-500">
          {service.title}
        </h3>

        {/* Description */}
        <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-500">
          {service.description}
        </p>

        {/* Decorative line */}
        <div className="mt-6 h-px bg-gradient-to-r from-[#c9a961]/0 via-[#c9a961]/50 to-[#c9a961]/0 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left" />
      </div>
    </motion.div>
  );
};

const Services = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <section className="relative py-24 md:py-32 bg-[#0f1419] overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg, #c9a961 0px, transparent 1px, transparent 40px)',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <span className="text-sm text-[#c9a961] uppercase tracking-wider mb-4 block">Services</span>
          <h2 className="font-serif text-4xl md:text-6xl text-white mb-6 leading-tight">
            Advisory Expertise
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Comprehensive legal and strategic counsel for sophisticated international clients
          </p>
        </motion.div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
